import { motion } from 'framer-motion';
import { Mic, MicOff, Maximize2, Minimize2 } from 'lucide-react';
import { CALL } from '@/constants/testIds';
import { useLanguage } from '../context/LanguageContext';

const QUALITY_COLOR = {
  good: 'bg-emerald-400',
  fair: 'bg-amber-400',
  poor: 'bg-red-500',
  unknown: 'bg-slate-300',
};

function qualityFromScore(score) {
  if (score === undefined || score === null) return 'unknown';
  if (score <= 2) return 'good';
  if (score <= 4) return 'fair';
  return 'poor';
}

export const NetworkDot = ({ score, testId }) => {
  const level = qualityFromScore(score);
  return (
    <span
      data-testid={testId}
      title={`Network quality: ${level}`}
      className={`inline-block h-2.5 w-2.5 rounded-full ${QUALITY_COLOR[level]} shadow-[0_0_6px_rgba(255,255,255,0.15)]`}
    />
  );
};

export const VideoTile = ({
  domId,
  name,
  isLocal = false,
  hasVideo = true,
  hasAudio = true,
  networkScore,
  testId,
  videoRef,
  fill = false,
  pinned = false,
  onClick,
}) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const { t } = useLanguage();
  return (
    <motion.div
      data-testid={testId}
      data-pinned={pinned}
      onClick={onClick}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className={`group relative overflow-hidden rounded-2xl border bg-slate-100 transition-colors duration-200 ${
        fill ? 'h-full w-full' : 'aspect-video'
      } ${pinned ? 'border-[#3B82F6]/60' : 'border-slate-200'} ${onClick ? 'cursor-pointer hover:border-slate-300' : ''}`}
    >
      <div id={domId} ref={videoRef} className="h-full w-full [&>video]:h-full [&>video]:w-full [&>video]:object-cover" />
      {!hasVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-100">
          <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[#3B82F6]/30 bg-[#3B82F6]/10 text-3xl font-semibold text-[#3B82F6]">
            {initial}
          </div>
          <span className="font-body text-xs text-slate-500">{t('call.cameraOff')}</span>
        </div>
      )}
      {onClick && (
        <div className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          {pinned ? (
            <Minimize2 className="h-3.5 w-3.5 text-white" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5 text-white" />
          )}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6">
        <span className="truncate font-body text-sm text-[#F8FAFC]" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
          {name}
          {isLocal ? t('call.you') : ''}
        </span>
        <div className="flex items-center gap-2">
          {!hasAudio && <MicOff className="h-3.5 w-3.5 text-red-400" strokeWidth={2} />}
          <NetworkDot score={networkScore} testId={CALL.networkIndicator(domId)} />
        </div>
      </div>
    </motion.div>
  );
};
